<!-- 新闻列表页 -->
<template>
  <div class="news-page">
    <Navbar />

    <!-- 页头 -->
    <section class="news-hero">
      <div class="news-hero-overlay"></div>
      <div class="news-hero-particles">
        <span v-for="n in 8" :key="n" class="particle" :style="{ '--i': n }"></span>
      </div>
      <div class="container">
        <div class="news-hero-content">
          <span class="section-tag">LATEST NEWS</span>
          <h1 class="news-hero-title">新闻资讯</h1>
          <p class="news-hero-desc">行业动态 · 品牌资讯 · 技术分享</p>
          <div class="hero-divider">
            <span class="line"></span>
            <span class="diamond"></span>
            <span class="line"></span>
          </div>
        </div>
      </div>
    </section>

    <!-- 搜索栏 -->
    <section class="news-search-bar">
      <div class="container">
        <div class="search-wrapper">
          <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            v-model="searchText"
            class="search-input"
            placeholder="搜索新闻标题…"
            @input="visible = true"
            @blur="handleBlur"
            @focus="visible = true"
          />
          <button v-if="searchText" class="search-clear" @click="clearSearch">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <!-- 搜索下拉 -->
          <div v-if="visible && searchnewslist.length" class="search-dropdown">
            <div
              v-for="data in searchnewslist"
              :key="data._id"
              class="search-result-item"
              @click="goDetail(data._id)"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d4af37" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
              <span>{{ data.title }}</span>
            </div>
          </div>
          <div v-else-if="visible && !searchText" class="search-dropdown">
            <el-empty description="请输入搜索内容" :image-size="40" />
          </div>
        </div>
      </div>
    </section>

    <!-- 分类标签 -->
    <section class="news-tabs-section">
      <div class="container">
        <div class="news-tabs">
          <button
            v-for="tab in tablist"
            :key="tab.name"
            class="tab-btn"
            :class="{ active: whichTab === tab.name }"
            @click="whichTab = tab.name"
          >
            <span>{{ tab.label }}</span>
            <span class="tab-count">{{ tabnews[tab.name]?.length || 0 }}</span>
          </button>
        </div>
      </div>
    </section>

    <!-- 新闻列表 -->
    <section class="news-list-section">
      <div class="container">
        <div class="news-grid">
          <div
            v-for="(item, i) in currentNewsList"
            :key="item._id"
            class="news-card"
            :style="{ animationDelay: i * 0.08 + 's' }"
            @click="goDetail(item._id)"
          >
            <div class="news-card-cover">
              <div
                class="news-cover-img"
                :style="{ backgroundImage: `url(${item.cover})` }"
              ></div>
              <span class="news-card-tag">{{ getCategoryLabel(item.category) }}</span>
            </div>
            <div class="news-card-body">
              <h3 class="news-card-title">{{ item.title }}</h3>
              <p class="news-card-excerpt">{{ item.introduction }}</p>
              <div class="news-card-footer">
                <span class="news-card-date">{{ whichTime(item.editTime) }}</span>
                <span class="news-card-views">
                  <i class="eye">👁</i>{{ item.viewCount || 0 }}
                </span>
                <span class="news-card-link">阅读更多</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="!currentNewsList.length" class="news-empty">
          <el-empty description="暂无相关新闻" />
        </div>
        
      </div>
    </section>

    <el-backtop :visibility-height="200" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import moment from 'moment'
import axios from 'axios'
import _ from 'lodash'
import Navbar from '@/components/Navbar.vue'

moment.locale('zh-cn')
const router = useRouter()

const searchText = ref('')
const visible = ref(false)
const newlist = ref([])
const whichTab = ref(1)
const tablist = [
  { label: '最新动态', name: 1 },
  { label: '产品预告', name: 2 },
  { label: '通知公告', name: 3 },
]

const tabnews = computed(() => _.groupBy(newlist.value, item => item.category))

const currentNewsList = computed(() => tabnews.value[whichTab.value] || [])

const searchnewslist = computed(() =>
  searchText.value
    ? newlist.value.filter(item => item.title.includes(searchText.value))
    : []
)

const getCategoryLabel = (cat) => {
  const map = { 1: '最新动态', 2: '产品预告', 3: '通知公告' }
  return map[cat] || '新闻'
}

const whichTime = (time) => {
  if (!time) return ''
  return moment(time).format('YYYY年MM月DD日')
}

const goDetail = (id) => {
  router.push(`/news/${id}`)
}

const handleBlur = () => {
  setTimeout(() => { visible.value = false }, 200)
}

const clearSearch = () => {
  searchText.value = ''
  visible.value = false
}

watch(searchText, () => {
  if (searchText.value) visible.value = true
})

// 获取新闻数据
onMounted(async () => {
  try {
    const res = await axios.get('/webapi/news/list')
    newlist.value = res.data.data || []
  } catch (e) {
    console.error('获取新闻列表失败:', e)
  }
})
</script>

<style scoped lang="scss">
// ===== 变量 =====
$gold: #d4af37;
$dark: #1a1a1a;
$gray: #6b7280;
$light-gray: #f8f9fa;
$border: #e5e7eb;

// ===== 全局 =====
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

// ===== 页头 =====
.news-hero {
  position: relative;
  min-height: 520px;
  background: linear-gradient(135deg, $dark 0%, #2a2a2a 100%);
  display: flex;
  align-items: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -20%;
    right: -5%;
    width: 700px;
    height: 700px;
    background: radial-gradient(circle, rgba(212,175,55,0.08) 0%, transparent 70%);
    border-radius: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, $gold, transparent);
    opacity: 0.3;
  }
}

.news-hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 100%);
}

.news-hero-particles {
  position: absolute;
  inset: 0;
  pointer-events: none;

  .particle {
    position: absolute;
    width: 4px;
    height: 4px;
    background: $gold;
    border-radius: 50%;
    opacity: 0;
    box-shadow: 0 0 8px $gold;
    animation: floatParticle 4s ease-in-out infinite;
    animation-delay: calc(var(--i) * 0.4s);
  }
}

@keyframes floatParticle {
  0%, 100% { transform: translateY(0) scale(1); opacity: 0.2; }
  50% { transform: translateY(-40px) scale(1.3); opacity: 0.6; }
}

.hero-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 28px 0;

  .line {
    width: 80px;
    height: 1px;
    background: linear-gradient(to right, transparent, $gold, transparent);
    opacity: 0.5;
  }

  .diamond {
    width: 6px;
    height: 6px;
    background: $gold;
    transform: rotate(45deg);
  }
}

.news-hero-content {
  position: relative;
  z-index: 2;
  padding: 60px 0;
  text-align: center;

  .section-tag {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 4px;
    text-transform: uppercase;
    color: $gold;
    display: block;
    margin-bottom: 16px;
  }

  .news-hero-title {
    font-size: 52px;
    font-weight: 800;
    color: #fff;
    letter-spacing: 2px;
    margin-bottom: 16px;
  }

  .news-hero-desc {
    font-size: 17px;
    color: rgba(255,255,255,0.6);
    letter-spacing: 1px;
  }
}

// ===== 搜索栏 =====
.news-search-bar {
  background: #fff;
  border-bottom: 1px solid $border;
  padding: 20px 0;
  position: sticky;
  top: 72px;
  z-index: 100;
}

.search-wrapper {
  position: relative;
  max-width: 560px;
  margin: 0 auto;

  .search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }

  .search-input {
    width: 100%;
    height: 48px;
    padding: 0 44px 0 44px;
    font-size: 15px;
    border: 1px solid $border;
    border-radius: 12px;
    background: $light-gray;
    color: $dark;
    outline: none;
    transition: all 0.3s ease;

    &::placeholder { color: #bbb; }

    &:focus {
      border-color: $gold;
      background: #fff;
      box-shadow: 0 0 0 3px rgba(212,175,55,0.1);
    }
  }

  .search-clear {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $border;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    color: #fff;
    transition: all 0.2s ease;

    &:hover { background: $dark; }
  }

  .search-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    background: #fff;
    border: 1px solid $border;
    border-radius: 12px;
    box-shadow: 0 12px 40px rgba(0,0,0,0.1);
    overflow: hidden;
    z-index: 200;
  }

  .search-result-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    font-size: 14px;
    color: $dark;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(212,175,55,0.06);
      color: $gold;
    }
  }
}

// ===== 分类标签 =====
.news-tabs-section {
  background: #fff;
  border-bottom: 1px solid $border;
  padding: 0;
}

.news-tabs {
  display: flex;
  gap: 0;
}

.tab-btn {
  position: relative;
  padding: 18px 32px;
  font-size: 15px;
  font-weight: 500;
  color: $gray;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 2px;
    background: $gold;
    transition: width 0.3s ease;
  }

  &:hover { color: $gold; }

  &.active {
    color: $gold;
    font-weight: 600;

    &::after { width: 100%; }
  }

  .tab-count {
    font-size: 12px;
    padding: 1px 8px;
    background: $light-gray;
    border-radius: 10px;
    color: $gray;
    font-weight: 500;
  }

  &.active .tab-count {
    background: rgba(212,175,55,0.12);
    color: $gold;
  }
}

// ===== 新闻列表 =====
.news-list-section {
  padding: 48px 0 80px;
  background: $light-gray;
  min-height: 400px;
}

.news-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
}

.news-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  border: 1px solid transparent;
  opacity: 0;
  transform: translateY(24px);

  &.news-card {
    animation: cardSlideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    animation-delay: v-bind('animationDelay');
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 48px rgba(0,0,0,0.08);
    border-color: rgba(212,175,55,0.2);

    .news-card-title { color: $gold; }
    .news-card-link { color: $gold; }
    .news-cover-img { transform: scale(1.06); }
  }

  .news-card-cover {
    position: relative;
    height: 180px;
    overflow: hidden;

    .news-cover-img {
      width: 100%;
      height: 100%;
      background-size: cover;
      background-position: center;
      transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .news-card-tag {
      position: absolute;
      top: 12px;
      left: 12px;
      padding: 3px 12px;
      font-size: 11px;
      font-weight: 600;
      color: #fff;
      background: rgba(26,26,26,0.75);
      backdrop-filter: blur(4px);
      border-radius: 20px;
      letter-spacing: 1px;
    }
  }

  .news-card-body {
    padding: 20px 24px 24px;

    .news-card-title {
      font-size: 17px;
      font-weight: 700;
      color: $dark;
      line-height: 1.45;
      margin-bottom: 10px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      transition: color 0.3s ease;
    }

    .news-card-excerpt {
      font-size: 14px;
      color: $gray;
      line-height: 1.7;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      margin-bottom: 16px;
    }

    .news-card-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;

      .news-card-date {
        font-size: 13px;
        color: #bbb;
      }

      .news-card-views {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        color: #bbb;

        .eye {
          font-size: 13px;
          font-style: normal;
        }
      }

      .news-card-link {
        font-size: 13px;
        font-weight: 600;
        color: $gray;
        display: flex;
        align-items: center;
        gap: 4px;
        transition: all 0.3s ease;

        &::after {
          content: '→';
          transition: transform 0.3s ease;
        }
      }
    }
  }
}

@keyframes cardSlideUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.news-empty {
  padding: 80px 0;
  text-align: center;
}

// ===== 响应式 =====
@media (max-width: 1024px) {
  .news-hero { min-height: 400px; }
  .news-hero-content { padding: 40px 0; }
  .news-grid { grid-template-columns: repeat(2, 1fr); }
  .news-hero-title { font-size: 36px; }
}

@media (max-width: 768px) {
  .news-hero { min-height: 340px; margin-top: 72px; }
  .news-hero-title { font-size: 32px; }
  .news-hero-content { padding: 40px 0; }
  .news-grid { grid-template-columns: 1fr; }
  .news-tabs { overflow-x: auto; }
  .tab-btn { padding: 14px 20px; font-size: 14px; white-space: nowrap; }
  .search-input {
    font-size: 14px;
    width: 200px !important;
    max-width: 200px !important;
    padding: 0 36px !important;
  }
  .search-wrapper { max-width: 260px; }
  .news-search-bar { padding: 10px 0; }
}

</style>
